<script lang="ts">
export interface Link {
  title: string;
  url: string;
}
</script>

<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";

import type { Editor } from "@tiptap/vue-3";

import TcIcon from "@/Components/Extend/TcIcon.vue";
import TcModal from "@/Components/UI/TcModal";
import TcInput from "@/Components/UI/TcInput.vue";

import { isValidString } from "@/Utils/String";
import { hasValue } from "@/Utils/Objects";

export interface LinkFieldProps {
  editor: Editor;
}

const props = defineProps<LinkFieldProps>();

const emit = defineEmits<{
  (e: "add", value: Link): void;
}>();

const { t } = useI18n();

const isOpen = ref<boolean>(false);
const isEdit = ref<boolean>(false);
const link = reactive<Link>({ title: "", url: "" });

const isComplete = computed(() => isValidString(link.title) && isValidString(link.url));

const handleOpen = () => {
  link.title = "";
  link.url = "";
  isEdit.value = false;

  if (props.editor.isActive("link")) {
    const linkAttrs = props.editor.getAttributes("link");
    const pos = props.editor.state.selection.$from;
    let fallbackText = "";

    if (hasValue(pos.nodeBefore)) {
      fallbackText += pos.nodeBefore.text;
    }

    if (hasValue(pos.nodeAfter)) {
      fallbackText += pos.nodeAfter.text;
    }

    if (isValidString(fallbackText)) {
      link.title = fallbackText;
      link.url = linkAttrs.href;
    }

    isEdit.value = true;
  } else {
    const { selection } = props.editor.state;
    const selectedText = props.editor.state.doc.textBetween(selection.from, selection.to, " ");

    if (isValidString(selectedText)) {
      link.title = selectedText;
    }
  }

  isOpen.value = true;
};

const handleClose = () => {
  isOpen.value = false;
};

const handleSave = () => {
  if (!isComplete.value) {
    if (props.editor.isActive("link")) {
      props.editor.chain().focus().extendMarkRange("link").unsetLink().run();
    }

    handleClose();

    return;
  }

  if (!isEdit.value) {
    emit("add", link);
  } else {
    props.editor.chain().focus().extendMarkRange("link").run();
    const selection = props.editor.state.selection;

    props.editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .insertContentAt(selection, { text: link.title })
      .setLink({ href: link.url })
      .run();
  }

  handleClose();
};
</script>

<template>
  <div class="link_field">
    <TcIcon name="Link" color="content-high" size="sm" @click="handleOpen" />

    <TcModal
      v-if="isOpen"
      :title="t('documents.editor.link.modal.title')"
      :rejectButtonText="t('documents.editor.link.modal.cancel.button')"
      :acceptButtonText="t('documents.editor.link.modal.save.button')"
      @acceptButtonClicked="handleSave"
      @rejectButtonClicked="handleClose"
    >
      <template #description>
        <TcInput
          :label="t('documents.editor.link.modal.link.text.label')"
          :placeholder="t('documents.editor.link.modal.link.text.placeholder')"
          v-model="link.title"
        />

        <TcInput
          :label="t('documents.editor.link.modal.link.url.label')"
          :placeholder="t('documents.editor.link.modal.link.url.placeholder')"
          v-model="link.url"
        />
      </template>
    </TcModal>
  </div>
</template>

<style scoped lang="scss"></style>
